import { Box, Divider, Skeleton } from '@mui/material'
import { blueGrey } from '@mui/material/colors'

const classes = {
    content: {
        backgroundColor: 'background.paper',
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.grey[400]}`,
        width: '100%',
        marginBottom: 3,
        padding: 3,
    },
    divider: {
        borderStyle: 'dashed',
        borderColor: blueGrey[200],
    },
}

/**
 * TbxQuoteHeaderSkeleton - Loading skeleton for QuoteHeader component
 *
 * Displays skeleton placeholders matching the quote header structure:
 * - Status chips row with action buttons
 * - Customer/Contact selection and quote details
 * - Shipping options section
 *
 * @returns {JSX.Element}
 */
const TbxQuoteHeaderSkeleton = () => {
    return (
        <>
            {/* Main header section */}
            <Box
                sx={[
                    {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                    },
                    classes.content,
                ]}
            >
                {/* Status chips and action buttons row */}
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    {/* Left side - Status chips */}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                        }}
                    >
                        <Skeleton
                            height={32}
                            sx={{ borderRadius: '16px' }}
                            variant="rectangular"
                            width={100}
                        />
                        <Skeleton
                            height={32}
                            sx={{ borderRadius: '16px' }}
                            variant="rectangular"
                            width={90}
                        />
                        <Skeleton
                            height={32}
                            sx={{ borderRadius: '16px' }}
                            variant="rectangular"
                            width={85}
                        />
                        <Skeleton
                            height={32}
                            sx={{ borderRadius: '16px' }}
                            variant="rectangular"
                            width={180}
                        />
                    </Box>

                    {/* Right side - Action buttons */}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                        }}
                    >
                        <Skeleton
                            height={36}
                            sx={{ borderRadius: '4px' }}
                            variant="rectangular"
                            width={140}
                        />
                        <Skeleton
                            height={36}
                            sx={{ borderRadius: '4px' }}
                            variant="rectangular"
                            width={120}
                        />
                    </Box>
                </Box>

                {/* Customer/Contact and Details row */}
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 3,
                    }}
                >
                    {/* Customer/Contact section */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            minWidth: 300,
                        }}
                    >
                        <Skeleton
                            height={56}
                            sx={{ borderRadius: '4px' }}
                            variant="rectangular"
                            width="100%"
                        />
                        <Skeleton
                            height={56}
                            sx={{ borderRadius: '4px' }}
                            variant="rectangular"
                            width="100%"
                        />
                    </Box>

                    <Divider
                        orientation="vertical"
                        sx={classes.divider}
                        flexItem
                    />

                    {/* Quote details section */}
                    <Box
                        sx={{
                            display: 'flex',
                            flex: 1,
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                            }}
                        >
                            <Skeleton
                                height={56}
                                sx={{ borderRadius: '4px' }}
                                variant="rectangular"
                                width={200}
                            />
                            <Skeleton
                                height={56}
                                sx={{ borderRadius: '4px' }}
                                variant="rectangular"
                                width={200}
                            />
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                            }}
                        >
                            <Skeleton
                                height={56}
                                sx={{ borderRadius: '4px' }}
                                variant="rectangular"
                                width={150}
                            />
                            <Skeleton
                                height={56}
                                sx={{ borderRadius: '4px' }}
                                variant="rectangular"
                                width={150}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Shipping options section */}
            <Box sx={classes.content}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    <Skeleton
                        height={24}
                        sx={{ borderRadius: '4px' }}
                        variant="rectangular"
                        width={150}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                        }}
                    >
                        <Skeleton
                            height={56}
                            sx={{ borderRadius: '4px' }}
                            variant="rectangular"
                            width={250}
                        />
                        <Skeleton
                            height={56}
                            sx={{ borderRadius: '4px' }}
                            variant="rectangular"
                            width={200}
                        />
                    </Box>
                </Box>
            </Box>
        </>
    )
}

export default TbxQuoteHeaderSkeleton
